import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import {
  HubConnection,
  HubConnectionBuilder,
  HubConnectionState,
  LogLevel,
  type ILogger,
} from "@microsoft/signalr";
import { getDebugLogger } from "../utils/debugLogger";

const log = getDebugLogger("signalr:connection");

// Forwards every internal signalr-client log line (negotiate, transport
// selection, handshake, keep-alive, close reason) to our debug sink. Passing
// a custom ILogger disables the client's own level filtering, so all of this
// is silent unless the host has enabled the "signalr:connection" scope.
const clientTraceLogger: ILogger = {
  log: (logLevel, message) => {
    log(`[client:${LogLevel[logLevel]}] ${message}`);
  },
};

// Waits between reconnect attempts. The provider runs its own loop instead of
// withAutomaticReconnect(): SignalR's default schedule ends in a `null` that
// gives up after ~42 seconds, which a laptop waking from sleep burns through
// before its network is back, leaving the board dead until a token renewal
// happened to rebuild the connection. This loop stays on the last delay forever.
const RECONNECT_DELAYS_MS = [0, 2_000, 5_000, 10_000, 20_000, 30_000];

// A connection has to stay up this long before a drop resets the back-off.
// Without it, a server that accepts the connection and then closes it straight
// away would be reconnected to in a tight loop.
const STABLE_CONNECTION_MS = 10_000;

const getReconnectDelayMs = (failedAttempts: number): number =>
  RECONNECT_DELAYS_MS[Math.min(failedAttempts, RECONNECT_DELAYS_MS.length - 1)];

export interface SignalRContextValue {
  connection: HubConnection | null;
  isConnected: boolean;
  error: Error | null;
}

const SignalRContext = createContext<SignalRContextValue | undefined>(
  undefined,
);

export interface SignalRProviderProps {
  children: ReactNode;
  hubUrl: string | null;
  isAuthenticated: boolean;
  /**
   * Called on every negotiate, reconnects included, so a connection that
   * outlives its access token comes back with a current one. It may refresh
   * the token first. A new function does not rebuild the connection.
   */
  getAccessToken: () => Promise<string | null> | string | null;
  autoReconnect?: boolean;
  /**
   * Lets the host report that the app is usable again, e.g. the tab became
   * visible or the network came back. Registers the callback and returns an
   * unsubscribe. A connection waiting out its back-off retries immediately.
   */
  subscribeToResume?: (onResume: () => void) => () => void;
}

export const SignalRProvider: React.FC<SignalRProviderProps> = ({
  children,
  hubUrl,
  isAuthenticated,
  getAccessToken,
  autoReconnect = true,
  subscribeToResume,
}) => {
  const [connection, setConnection] = useState<HubConnection | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const getAccessTokenRef = useRef(getAccessToken);
  const reconnectNowRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    getAccessTokenRef.current = getAccessToken;
  }, [getAccessToken]);

  useEffect(() => {
    if (!subscribeToResume) {
      return undefined;
    }

    return subscribeToResume(() => reconnectNowRef.current?.());
  }, [subscribeToResume]);

  useEffect(() => {
    if (!isAuthenticated || !hubUrl) {
      log("Skipping connection setup", {
        isAuthenticated,
        hubUrl,
      });
      setConnection(null);
      setIsConnected(false);
      return undefined;
    }

    log("Building connection", { hubUrl, autoReconnect });

    const hubConnection = new HubConnectionBuilder()
      .withUrl(hubUrl, {
        accessTokenFactory: async () =>
          (await getAccessTokenRef.current()) ?? "",
      })
      .configureLogging(clientTraceLogger)
      .build();

    let isDisposed = false;
    let failedAttempts = 0;
    let connectedAt: number | null = null;
    let reconnectTimer: ReturnType<typeof setTimeout> | undefined;

    const connect = async (): Promise<void> => {
      if (isDisposed || hubConnection.state !== HubConnectionState.Disconnected) {
        return;
      }

      log("Starting connection", { failedAttempts });

      try {
        await hubConnection.start();
      } catch (startError) {
        if (isDisposed) {
          return;
        }

        const failure =
          startError instanceof Error ? startError : new Error(String(startError));
        setIsConnected(false);
        setError(failure);
        log("Failed to start connection", { startError: failure, failedAttempts });

        if (autoReconnect) {
          scheduleReconnect();
        } else {
          console.error("[SignalR] Failed to start connection:", failure);
        }
        return;
      }

      if (isDisposed) {
        return;
      }

      connectedAt = Date.now();
      setIsConnected(true);
      setError(null);
      log("Connection started", { connectionId: hubConnection.connectionId });
    };

    const scheduleReconnect = (): void => {
      const delayMs = getReconnectDelayMs(failedAttempts);
      failedAttempts += 1;
      log("Reconnect scheduled", { delayMs, failedAttempts });
      reconnectTimer = setTimeout(() => {
        reconnectTimer = undefined;
        void connect();
      }, delayMs);
    };

    hubConnection.onclose((closeError) => {
      if (isDisposed) {
        return;
      }

      setIsConnected(false);
      setError(closeError ?? null);
      log("Connection closed", { closeError });

      if (!autoReconnect) {
        if (closeError) {
          console.error("[SignalR] Connection closed with error:", closeError);
        }
        return;
      }

      if (closeError) {
        console.warn("[SignalR] Connection lost, reconnecting:", closeError);
      }

      const stayedUp =
        connectedAt !== null && Date.now() - connectedAt >= STABLE_CONNECTION_MS;
      connectedAt = null;
      if (stayedUp) {
        failedAttempts = 0;
      }
      scheduleReconnect();
    });

    // Only acts while waiting out a back-off: a live connection needs nothing,
    // and a start already in flight must not be doubled up.
    reconnectNowRef.current = () => {
      if (isDisposed || reconnectTimer === undefined) {
        return;
      }

      log("Resumed while disconnected, reconnecting now", { failedAttempts });
      clearTimeout(reconnectTimer);
      reconnectTimer = undefined;
      void connect();
    };

    setConnection(hubConnection);
    void connect();

    return () => {
      isDisposed = true;
      reconnectNowRef.current = null;
      clearTimeout(reconnectTimer);
      log("Tearing down connection", { state: hubConnection.state });
      void hubConnection.stop();
    };
  }, [autoReconnect, hubUrl, isAuthenticated]);

  const value: SignalRContextValue = {
    connection,
    isConnected,
    error,
  };

  return (
    <SignalRContext.Provider value={value}>{children}</SignalRContext.Provider>
  );
};

export const useSignalR = (): SignalRContextValue => {
  const context = useContext(SignalRContext);
  if (!context) {
    throw new Error("useSignalR must be used within a SignalRProvider");
  }
  return context;
};
